import { caseTransfer } from "./cases.js";
import { mergeOptions, prepareRequestBody, validateParameters, getValidatorAdapter } from "./utils.js";
import { Case, HTTPError, HTTPMethod, RequestError, ResponseError } from "./types.js";
import { useRuntimeConfig } from "#imports";
export class CommonStrictFetch {
  _config = {
    options: {},
    orderRequests: {},
    orderHooks: {},
    methodSignals: {}
  };
  get config() {
    return this._config;
  }
  get additionalHeaders() {
    return {};
  }
  init = (options) => {
    Object.assign(this.config.options, options);
  };
  hooks = {
    subscribe: (key, handler) => {
      this.config.orderHooks[key] = [...this.config.orderHooks[key] || [], handler];
    },
    unsubscribe: (key, handler) => {
      if (this.config.orderHooks[key]) {
        this.config.orderHooks[key] = this.config.orderHooks[key].filter((el) => el !== handler);
      }
    }
  };
  setupDefaultOptions = (options) => {
    const mergedOptions = mergeOptions(options);
    if (!mergedOptions.method) mergedOptions.methodKey = crypto.randomUUID();
    return mergedOptions;
  };
  execute = async (url, { fetch = $fetch, orderKey, groupKey: _, methodKey, selfInterrupted, ...options }, pluginOptions) => {
    methodKey = typeof methodKey === "function" ? methodKey(options) : methodKey;
    if (methodKey && pluginOptions) {
      pluginOptions.orderHooks[`method:${methodKey}:start`]?.forEach((el) => el());
      if (selfInterrupted) {
        pluginOptions.methodSignals[methodKey]?.abort();
        pluginOptions.methodSignals[methodKey] = new AbortController();
      }
    }
    if (orderKey && pluginOptions) {
      await new Promise((r) => {
        if (pluginOptions.orderRequests[orderKey]) pluginOptions.orderRequests[orderKey].push(r);
        else {
          pluginOptions.orderHooks[`order:${orderKey}:start`]?.forEach((el) => el());
          pluginOptions.orderRequests[orderKey] = [r];
        }
        if (pluginOptions.orderRequests[orderKey][0] === r) r(true);
      });
    }
    return fetch(url, {
      signal: methodKey ? pluginOptions?.methodSignals[methodKey]?.signal : null,
      ...options
    }).catch((error) => {
      if (options.catch) return options.catch(error);
      throw error;
    }).finally(() => {
      if (!pluginOptions) return;
      if (methodKey) {
        pluginOptions.orderHooks[`method:${methodKey}:finish`]?.forEach((el) => el());
        delete pluginOptions.methodSignals[methodKey];
      }
      if (orderKey) {
        pluginOptions.orderRequests[orderKey]?.shift();
        if (!pluginOptions.orderRequests[orderKey]?.length) {
          delete pluginOptions.orderRequests[orderKey];
          pluginOptions.orderHooks[`order:${orderKey}:finish`]?.forEach((el) => el());
        } else pluginOptions.orderRequests[orderKey]?.[0]?.(true);
      }
    });
  };
  prepare = ({
    url,
    method = HTTPMethod.GET,
    options = {},
    ...rest
  }) => {
    options = this.setupDefaultOptions(options);
    const executor = async (parameters, additionalOptions = {}) => {
      const runtimeConfig = useRuntimeConfig();
      const validator = getValidatorAdapter(rest.schemas);
      const additionalHeaders = this.additionalHeaders;
      const config = this.config;
      const baseOptions = mergeOptions(
        runtimeConfig.public.strictFetchOptions,
        config.options,
        options,
        additionalOptions
      );
      try {
        const [body, params, query] = await Promise.all([
          validateParameters(validator, parameters, "body"),
          validateParameters(validator, parameters, "params"),
          validateParameters(validator, parameters, "query")
        ]);
        const data = await this.execute(
          typeof url === "function" ? url(params) : url,
          mergeOptions(baseOptions, {
            headers: additionalHeaders,
            method,
            params: query,
            body: prepareRequestBody(body, baseOptions),
            onRequestError(context) {
              throw new RequestError(
                `Fetch request error: ${context.error.message || 'Empty common "message" parameter'}`
              ).from(context).with(context.error);
            },
            onResponseError(context) {
              throw new ResponseError(
                `Fetch response error: ${context.response?._data?.message || context.response?.statusText || context.error?.message || "Empty error message"}`
              ).from(context, context.response?._data).with(context.error);
            }
          }),
          config
        );
        const responseData = caseTransfer(data, Case.CAMEL);
        return validator?.response ? await validator.response.validate(responseData) : responseData;
      } catch (e) {
        const error = e;
        if (error.name !== HTTPError.AbortError) {
          baseOptions.onError?.(error);
        }
        throw error;
      }
    };
    executor.schemas = rest.schemas;
    return executor;
  };
}
